import { format, parseISO } from "date-fns";

import { DATE_PRESETS, toLocalYmd, TYPE_FILTER_OPTIONS } from "./constants";

import type { DatePreset } from "./constants";

export const parseFilterDate = (
  raw: string | undefined
): string | undefined => {
  if (!raw || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) return undefined;
  const [y, m, d] = raw.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  if (
    date.getFullYear() !== y ||
    date.getMonth() !== m - 1 ||
    date.getDate() !== d
  )
    return undefined;
  return raw;
};

export const toStartOfDay = (date: string): string =>
  date.includes("T") ? date : `${date}T00:00:00`;

export const toEndOfDay = (date: string): string =>
  date.includes("T") ? date : `${date}T23:59:59.999999`;

const FALLBACK_DATE_FORMAT = "m/d/Y";
const FLATPICKR_TOKEN_MAP: Record<string, string> = {
  year: "Y",
  month: "m",
  day: "d",
};

/**
 * Derives a flatpickr-compatible date format string from the browser locale.
 * Falls back to "m/d/Y" (MM/DD/YYYY) when Intl is unavailable.
 */
export const getLocaleDateFormat = (): string => {
  try {
    const parts = new Intl.DateTimeFormat(navigator.language, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(new Date(2000, 0, 15));

    return parts.map((p) => FLATPICKR_TOKEN_MAP[p.type] ?? p.value).join("");
  } catch {
    return FALLBACK_DATE_FORMAT;
  }
};

/**
 * Formats a YYYY-MM-DD string using the browser's locale (e.g. "01/15/2026"
 * for en-US, "15/01/2026" for en-GB). Returns the raw string on failure.
 */
export const formatLocaleDate = (iso: string): string => {
  try {
    const [year, month, day] = iso.split("-").map(Number);
    const d = new Date(year, month - 1, day);
    if (Number.isNaN(d.getTime())) return iso;
    return new Intl.DateTimeFormat(navigator.language, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(d);
  } catch {
    return iso;
  }
};

export const hasFutureDate = (...dates: (string | undefined)[]): boolean => {
  const today = toLocalYmd(new Date());
  return dates.some((d) => d !== undefined && d > today);
};

export const TYPE_VALID_VALUES: string[] = TYPE_FILTER_OPTIONS.map(
  (o) => o.value
);

/**
 * Find the preset whose date range matches the given start/end values.
 * Returns `null` when no preset matches (i.e. a custom range).
 *
 * By default compares against the live `minDate()`/`maxDate()` on each
 * preset. Pass `getRange` to compare against pre-computed (frozen) values
 * instead — see `DateRangeFilterPanel` for why this matters at midnight.
 */
export const findMatchingPreset = <T extends DatePreset>(
  from: string | undefined,
  to: string | undefined,
  presets: T[] = DATE_PRESETS as T[],
  getRange: (p: T) => { min: string; max: string } = (p) => ({
    min: p.minDate(),
    max: p.maxDate(),
  })
): T | null => {
  if (!from || !to) return null;
  return (
    presets.find((p) => {
      const range = getRange(p);
      return range.min === from && range.max === to;
    }) ?? null
  );
};

/**
 * Format a YYYY-MM-DD date range for display.
 *
 * Same year:      "May 13 - Jun 13, 2026"
 * Different year: "May 13, 2025 - Jun 13, 2026"
 */
export const formatDateRange = (from: string, to: string): string => {
  const fromDate = parseISO(from);
  const toDate = parseISO(to);
  const sameYear = fromDate.getFullYear() === toDate.getFullYear();

  const fromStr = format(fromDate, sameYear ? "MMM d" : "MMM d, yyyy");
  const toStr = format(toDate, "MMM d, yyyy");

  return `${fromStr} - ${toStr}`;
};

/**
 * Derive the summary caption text based on filter state.
 *
 * - No filters active → "Last 30 days" (the default window)
 * - Filters active, no date range → "All time · Filtered"
 * - Date matches a preset → preset label (e.g. "Last 90 days")
 * - Custom date range → formatted range (e.g. "May 13 - Jun 13, 2026")
 */
export const buildSummaryCaption = (
  isFiltered: boolean,
  settledFrom: string | undefined,
  settledTo: string | undefined,
  getString: (id: string) => string
): string => {
  if (!isFiltered) {
    return getString("live-ledger-summary-caption-default");
  }

  if (settledFrom && settledTo) {
    const preset = findMatchingPreset(settledFrom, settledTo);
    if (preset) {
      return getString(preset.ftlId);
    }
    return formatDateRange(settledFrom, settledTo);
  }

  return getString("live-ledger-summary-caption-filtered");
};
